import { StatsService } from '../../stats/stats.service';
import { WordcloudService } from "./wordcloud.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { Component, Inject, Input, LOCALE_ID, OnChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChartConstructorType } from "highcharts-angular";
import { GraphUtils } from "../utils.service";


@Component({
    selector: "wordcloud",
    templateUrl: "./wordcloud.component.html",
    styleUrls: ['./wordcloud.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WordcloudComponent implements OnInit, OnChanges {
    selected_language: any;
    language: string = localStorage.getItem('wc_language');
    nonFilteredWords: any[] = [];
    languages: any;
    wordCloudConfigData: any;
    locale: string;
    protected languageCtrl = new FormControl();

    @Input() query: string;
    constructor(
        @Inject(LOCALE_ID) locale: string,
        private graphUtils: GraphUtils,
        private statsService: StatsService,
        private wordcloudService: WordcloudService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
    ) {
        this.locale = locale;
        this.languageCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap(value => {
                this.change_language(value);
            }),
            takeUntilDestroyed()
          ).subscribe();
    }

    chartOptions$ = new BehaviorSubject<Highcharts.Options>({});
    chartConstructor: ChartConstructorType = 'chart';

    ngOnInit(): void {
        if (!this.language) {
           const detectedLanguage = this.locale;
           this.setLanguage(detectedLanguage);
        }
    }
    ngOnChanges() { 
        if(this.language && this.query) {
            this.updateWordCloud();
        }
    }

    wordCloudConfig(wcdata) {
        return {
            chart: {
                backgroundColor: this.graphUtils.graphBackgroundColor,
            },
            title: {
                text: ''
            },
            credits: {
                text: 'TeamMood.com',
                href: 'https://www.teammood.com',
            },
            series: [{
                type: 'wordcloud',
                data: wcdata,
                name: 'Occurrences',
                rotation: {
                    from: 0,
                    to: 0,
                    orientations: 1
                },
                tooltip: {
                    headerFormat: '',
                    pointFormatter: (function(component) { // use to get access to translateService
                        return function() {
                            const point = this; // this refers to the hovered point
                            const details = point.details;
                            let detailText = '';
                            if(details && component){
                                details.forEach(detail => {
                                    detailText += '- ' + component.translateService.instant(detail.mood);
                                    detailText += ': ';
                                    detailText += detail.occurence;
                                    detailText += ' ' + component.translateService.instant('analytics.wordcloud.times')+ ' <br>';
                                    if (detail.comments) {
                                        detail.comments.forEach(comment => {
                                            detailText += '<i class="wordcloud_tooltip_comment">"' + comment + '"</i><br>';
                                        })
                                    }
                                })
                            }
                            return '<b>' + component.translateService.instant('analytics.wordcloud.word') + ': "' + point.name +'"<br>'
                            + component.translateService.instant('moods_average') + ': '+ point.averageMood+'<sup>/10</sup>' + '</b>'
                            + '<br>--- <br>'
                            + component.translateService.instant('analytics.wordcloud.word_details') + ': <br>'
                            + detailText              
                        }
                    })(this)
                }
            }],
            tooltip: {
                outside: true,
                hideDelay: 4000,
                useHTML: true,
                backgroundColor: "rgba(255,255,255,1)",
                borderColor: '#ddd',
            },
        };
    }

    setLanguage(newLang) {
        localStorage.setItem('wc_language', newLang);
        this.language = newLang;
        this.selected_language = newLang;
    }

    

    async updateWordCloud() {
        const wordcloud= await this.statsService.wordcloud(this.query, this.language);
        if (wordcloud.error) {
            this.snackBarService.error(wordcloud.error);
            this.nonFilteredWords = [];
            this.wordCloudConfigData = this.wordCloudConfig(this.nonFilteredWords);
        } else {
            this.languages = wordcloud.availableLanguages;

            const words = wordcloud.words;
            this.nonFilteredWords = this.wordcloudService.setWordData(words, null, null);
            this.wordCloudConfigData = this.wordCloudConfig(this.nonFilteredWords);

            if (wordcloud.availableLanguages.indexOf(this.language) === -1) {
                this.selected_language = "en";
            } else {
                this.selected_language = this.language;
            }
            this.languageCtrl.setValue(this.selected_language);
        }
        this.chartOptions$.next(this.wordCloudConfigData);

    }

    //word cloud filters
    moodFilters = [
        { mood: 'excellent', color: '#1abc9c', active: true},
        { mood: 'good', color: '#5c91df', active: true},
        { mood: 'average', color: '#f1c40f', active: true},
        { mood: 'hard', color: '#ff7a23', active: true},
        { mood: 'bad', color: '#b33c6b', active: true}
    ];

    select(filter){
        filter.active = !filter.active;
        this.filterWords();
    }

    filterWords(){
        let filteredWords = [];
        const activeFilters = this.moodFilters.filter(filter => filter.active);
        if(activeFilters.length === 0){
            this.wordCloudConfigData = this.wordCloudConfig([]);
        }else{
            for(let filter of activeFilters){
                let words = [];
                words = this.nonFilteredWords.filter((word) => {    
                    return word.color === filter.color
                });
                filteredWords = [...filteredWords, ...words];
            }
            this.wordCloudConfigData = this.wordCloudConfig(filteredWords);
        }
        this.chartOptions$.next(this.wordCloudConfigData);
    }


    change_language (selectedLanguage) {
        this.setLanguage(selectedLanguage);
        this.updateWordCloud();
    };

}